import { allBlogPosts } from "@/lib/allBlogPosts";
import { deduplicatePosts } from "@/lib/blogPostProcessor";
import { normalizePostDates, getNormalizedDate } from "@/lib/dateNormalizer";
import {
  generateFAQs,
  generateLastUpdated,
  generateHowTo,
} from "@/lib/faqGenerator";
import { categoryToSlug } from "@/lib/categorySlug";

/**
 * Formats an ISO calendar date (YYYY-MM-DD) as a timezone-aware ISO 8601
 * timestamp in America/Los_Angeles at 08:00 local time, with the correct
 * DST offset for that specific date (PDT -07:00 / PST -08:00).
 *
 * Used for datePublished / dateModified in Article/BlogPosting schema and
 * Open Graph article dates so all date surfaces are consistent and
 * correctly attributed to the Pacific publishing timezone.
 */
function toLosAngelesISO8601(isoDate: string, hour = 8, minute = 0): string {
  // We want a wall-clock time of `hour:minute` in America/Los_Angeles for the
  // given calendar date. Determine the Pacific offset for that date first by
  // probing with an instant, then construct the UTC instant so that the LA
  // wall clock reads correctly: UTC = local - offset.
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return new Date(isoDate).toISOString();

  // Probe the offset for the date using 12:00 UTC (avoids day-boundary edge cases).
  const probe = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    timeZoneName: "shortOffset",
  });
  const parts = dtf.formatToParts(probe);
  const offRaw = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  const offMatch = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(offRaw);
  let sign = -1;
  let offsetMinutes = 7 * 60; // default PDT
  if (offMatch) {
    sign = offMatch[1] === "+" ? 1 : -1;
    offsetMinutes =
      sign *
      (parseInt(offMatch[2], 10) * 60 + parseInt(offMatch[3] || "0", 10));
  }
  // UTC instant = LA wall clock minus offset (offset is LA - UTC).
  const utcMs =
    Date.UTC(y, m - 1, d, hour, minute, 0) - offsetMinutes * 60 * 1000;
  const inst = new Date(utcMs);

  // Format the resulting instant back to LA wall clock to confirm + emit.
  const out = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZoneName: "shortOffset",
  }).formatToParts(inst);
  const get = (t: string) => out.find((p) => p.type === t)?.value ?? "";
  let off = get("timeZoneName") || "";
  const om = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(off);
  let offset = "-07:00";
  if (om) {
    const hh = om[2].padStart(2, "0");
    const mm = (om[3] || "0").padStart(2, "0");
    offset = `${om[1]}${hh}:${mm}`;
  }
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get(
    "minute",
  )}:${get("second")}${offset}`;
}

const BASE_URL = "https://marjohnrobillo.me";
const DEFAULT_OG_IMAGE =
  "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png";

const CATEGORY_OG_IMAGES: Record<string, string> = {
  "Compensation & Logistics":
    "https://vibe.filesafe.space/1790177007883282700/assets/b8f9bc46-1e63-4e50-9771-41576d795a3e.png",
  "Hiring Strategy":
    "https://vibe.filesafe.space/1790177007883282700/assets/78e15f41-514b-4e81-934b-6f5c3db1cdbb.png",
  "Systems Architecture":
    "https://vibe.filesafe.space/1790177007883282700/assets/15f0674d-96a8-488b-ad9f-13f71220388b.png",
  "Revenue Operations":
    "https://vibe.filesafe.space/1790177007883282700/assets/fd230d69-9347-4455-afc7-3dcf05bccb26.png",
  "Agency Scaling":
    "https://vibe.filesafe.space/1790177007883282700/assets/ea14b10c-002b-4f2a-82f8-88d87aef2a30.png",
  "Automation Strategy":
    "https://vibe.filesafe.space/1790177007883282700/assets/a71d2e65-6ebe-4131-bcd2-f8f65af97a1a.png",
  "Hiring Guide":
    "https://vibe.filesafe.space/1790177007883282700/assets/8b561e95-35d7-4475-9c67-cb3b47043531.png",
  "Advanced Automation":
    "https://vibe.filesafe.space/1790177007883282700/assets/39b42fa5-5a9d-4292-a21c-cbf7fde5a199.png",
  "Artificial Intelligence":
    "https://vibe.filesafe.space/1790177007883282700/assets/ed648153-9178-4f56-b785-14f7325d4657.png",
  "Conversion Optimization":
    "https://vibe.filesafe.space/1790177007883282700/assets/0dc5577d-e7c6-47b8-9ee4-d7710fb2e359.png",
  "CRM Architecture":
    "https://vibe.filesafe.space/1790177007883282700/assets/0e238e68-857b-4fda-b435-1dc2727f9816.png",
  "Management & Tracking":
    "https://vibe.filesafe.space/1790177007883282700/assets/a3302622-69e3-4261-9d97-4f0bd7b60198.png",
  "Infrastructure Scaling":
    "https://vibe.filesafe.space/1790177007883282700/assets/f617baba-fbd6-4689-911d-a82ea3f721e9.png",
};

export function getCategoryOGImage(category: string): string {
  return CATEGORY_OG_IMAGES[category] || DEFAULT_OG_IMAGE;
}

export interface PageSeoInput {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  /** Optional JSON-LD objects to emit as ld+json scripts. */
  schema?: Record<string, unknown>[];
  /** Optional robots meta directives. Defaults to a generous index/follow. */
  robots?: string;
  /** Optional hreflang alternates for regional/language variants. */
  hreflang?: { hreflang: string; href: string }[];
}

const AUTHOR = {
  "@type": "Person",
  "@id": "https://marjohnrobillo.me/authors/marjohn-robillo#person",
  name: "Marjohn B. Robillo",
  url: "https://marjohnrobillo.me/authors/marjohn-robillo",
};

/** BreadcrumbList schema from a list of { name, path } crumbs. */
export function breadcrumbSchema(
  crumbs: { name: string; path: string }[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: `${BASE_URL}${c.path}`,
    })),
  };
}

/** Service schema for a service/geo page. */
export function serviceSchema(opts: {
  name: string;
  description: string;
  path: string;
  serviceType: string[];
  areaServed?: string[];
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: opts.name,
    description: opts.description,
    url: `${BASE_URL}${opts.path}`,
    provider: {
      "@type": "Person",
      name: "Marjohn B. Robillo",
      url: BASE_URL,
      jobTitle: "Revenue Operations Architect",
    },
    areaServed: (
      opts.areaServed ?? ["United States", "Canada", "Australia"]
    ).map((a) => ({ "@type": "Country", name: a })),
    serviceType: opts.serviceType,
    audience: { "@type": "BusinessAudience" },
  };
}

/** FAQPage schema from a list of { question, answer } pairs. */
export function faqPageSchema(
  faqs: { question: string; answer: string }[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
}

/** Organization schema — entity for E-E-A-T / knowledge panel signals. */
export function organizationSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Marjohn Robillo",
    url: BASE_URL,
    founder: AUTHOR,
    description:
      "Revenue systems architecture — CRM, automation, and revenue operations engineering for marketing agencies.",
  };
}

/** Person schema — author credentials for E-E-A-T.
 * References the canonical @id entity anchor at
 * /authors/marjohn-robillo#person so Google and LLMs consolidate
 * all articles under one entity. */
export function personSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": "https://marjohnrobillo.me/authors/marjohn-robillo#person",
    name: "Marjohn B. Robillo",
    url: "https://marjohnrobillo.me/authors/marjohn-robillo",
    jobTitle: "Revenue Operations Architect",
    description:
      "Revenue Operations Architect specializing in CRM automation, n8n workflow engineering, and AI-driven revenue systems. MPA graduate, Licensed Professional Teacher (LPT), MUN-UNESCO Diplomat (Bali 2022), and certified GoHighLevel and HubSpot Specialist.",
    knowsAbout: [
      "Revenue Operations",
      "GoHighLevel Automation",
      "HubSpot CRM",
      "n8n Workflow Development",
      "Zapier Integrations",
      "Make.com Automation",
      "Voice AI Automation",
      "Revenue Engineering",
    ],
    alumniOf: {
      "@type": "EducationalOrganization",
      name: "Master in Public Administration (MPA)",
    },
    hasOccupation: {
      "@type": "Occupation",
      name: "Revenue Operations Architect",
    },
    sameAs: [
      "https://www.linkedin.com/in/marjohnrobillo-com/",
      "https://marjohnrobillo.com",
      "https://zappify.io",
      "https://www.youtube.com/channel/UCRFFj1RQDEiHv3LGiDz1srA",
      "https://www.crunchbase.com/person/marjohn-robillo",
      "https://www.wikidata.org/wiki/Q141613874",
    ],
  };
}

/**
 * VideoObject schema — the 2026 GEO lever for Gemini (which extracts from
 * YouTube transcripts). Declares the channel's canonical explainer video so
 * Gemini and AI Overviews can lift it as a citation source. Used on the root
 * and service pages. See SEO_GEO_AUDIT_2026.md gap #45.
 */
export function videoObjectSchema(opts?: {
  name?: string;
  description?: string;
  uploadDate?: string;
  thumbnailUrl?: string;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name:
      opts?.name ??
      "Revenue Operations Architecture & AI Automation — Marjohn Robillo",
    description:
      opts?.description ??
      "Marjohn B. Robillo, Philippines-based Revenue Operations Architect, explains how CRM automation, n8n orchestration, and agentic Voice AI build revenue systems that run autonomously for agencies across the US, Canada, Australia, and the UK.",
    thumbnailUrl:
      opts?.thumbnailUrl ??
      "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png",
    uploadDate: opts?.uploadDate ?? "2025-01-15",
    contentUrl: "https://www.youtube.com/channel/UCRFFj1RQDEiHv3LGiDz1srA",
    embedUrl: "https://www.youtube.com/channel/UCRFFj1RQDEiHv3LGiDz1srA",
    publisher: {
      "@type": "Organization",
      name: "Marjohn Robillo",
      url: BASE_URL,
    },
    author: AUTHOR,
  };
}

/** ContactPage schema — discoverable contact method. */
export function contactPageSchema(): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    url: `${BASE_URL}/contact`,
    name: "Contact Marjohn Robillo",
    description:
      "Book a free discovery call or send a direct message to Marjohn Robillo, Revenue Operations Architect.",
    mainEntity: {
      "@type": "Person",
      name: "Marjohn B. Robillo",
      url: BASE_URL,
      email: "mailto:hello@marjohnrobillo.com",
      contactType: "Revenue Operations Architect",
    },
  };
}

/** WebPage schema — generic page with author + publisher for E-E-A-T. */
export function webPageSchema(opts: {
  name: string;
  description: string;
  path: string;
}): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: opts.name,
    description: opts.description,
    url: `${BASE_URL}${opts.path}`,
    author: AUTHOR,
    publisher: {
      "@type": "Organization",
      name: "Marjohn Robillo",
      url: BASE_URL,
    },
  };
}

export function buildPageHead({
  title,
  description,
  path,
  keywords,
  ogImage = DEFAULT_OG_IMAGE,
  ogType = "website",
  schema = [],
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  hreflang = [],
}: PageSeoInput) {
  const url = `${BASE_URL}${path}`;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { name: "robots", content: robots },
      ...(keywords ? [{ name: "keywords", content: keywords }] : []),
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: ogType },
      { property: "og:url", content: url },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: ogImage },
    ],
    links: [
      { rel: "canonical", href: url },
      ...hreflang.map((a) => ({
        rel: "alternate",
        hreflang: a.hreflang,
        href: a.href,
      })),
    ],
    scripts: schema.map((s) => ({
      type: "application/ld+json",
      children: JSON.stringify(s),
    })),
  };
}

export interface ResolvedBlogPost {
  post: (typeof allBlogPosts)[number];
  normalizedDate: string;
  faqs: ReturnType<typeof generateFAQs>;
  lastUpdated?: string;
  howTo: ReturnType<typeof generateHowTo>;
  ogImage: string;
}

const dedupedPosts = deduplicatePosts(allBlogPosts);
const dateMap = normalizePostDates(dedupedPosts);

export function resolveBlogPost(slug: string): ResolvedBlogPost | null {
  const post = dedupedPosts.find((p) => p.slug === slug);
  if (!post) return null;
  const normalizedDate = getNormalizedDate(post, dateMap);
  return {
    post,
    normalizedDate,
    faqs: generateFAQs(post),
    lastUpdated: generateLastUpdated(post),
    howTo: generateHowTo(post),
    ogImage: getCategoryOGImage(post.category),
  };
}

export function buildBlogPostHead(resolved: ResolvedBlogPost) {
  const { post, normalizedDate, lastUpdated, howTo, ogImage } = resolved;
  const truncatedTitle =
    post.seoMeta.title.length > 60
      ? post.seoMeta.title.substring(0, 57) + "..."
      : post.seoMeta.title;
  const url = `${BASE_URL}/blog/${post.slug}`;
  const datePublished = toLosAngelesISO8601(normalizedDate);
  const dateModified = toLosAngelesISO8601(lastUpdated || normalizedDate);

  const scripts: Record<string, unknown>[] = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      headline: post.title,
      description: post.seoMeta.description,
      image: ogImage,
      inLanguage: "en-US",
      author: AUTHOR,
      publisher: AUTHOR,
      datePublished,
      dateModified,
      keywords: post.seoMeta.keywords,
      wordCount: post.content.split(/\s+/).length,
      articleBody: post.content.replace(/<[^>]*>/g, "").substring(0, 5000),
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", ".prose > p:first-of-type"],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "TechArticle",
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      headline: post.title,
      description: post.seoMeta.description,
      image: ogImage,
      inLanguage: "en-US",
      author: AUTHOR,
      publisher: AUTHOR,
      datePublished,
      dateModified,
      proficiencyLevel: "Expert",
      dependencies: "CRM platform, n8n, or relevant automation tooling",
      audience: { "@type": "BusinessAudience" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: `${BASE_URL}/blog`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: post.category,
          item: `${BASE_URL}/blog/category/${categoryToSlug(post.category)}`,
        },
        { "@type": "ListItem", position: 4, name: post.title, item: url },
      ],
    },
  ];

  if (resolved.faqs.length > 0) {
    scripts.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: resolved.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    });
  }

  if (howTo) {
    scripts.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: howTo.name,
      step: howTo.steps.map((step) => ({
        "@type": "HowToStep",
        name: step.name,
        text: step.text,
      })),
    });
  }

  return {
    meta: [
      { title: truncatedTitle },
      { name: "description", content: post.seoMeta.description },
      {
        name: "robots",
        content:
          "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
      },
      { name: "keywords", content: post.seoMeta.keywords },
      { property: "og:title", content: truncatedTitle },
      { property: "og:description", content: post.seoMeta.description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { property: "og:image", content: ogImage },
      { property: "article:published_time", content: datePublished },
      { property: "article:modified_time", content: dateModified },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: truncatedTitle },
      { name: "twitter:description", content: post.seoMeta.description },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: scripts.map((s) => ({
      type: "application/ld+json",
      children: JSON.stringify(s),
    })),
  };
}
